import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { GraduationCap, BookOpen, Award, AlertCircle, CheckCircle } from "lucide-react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock LMS data
const lmsModules = [
  { name: "Basic Assessment", enrolled: 280, completed: 245, pass_rate: 92 },
  { name: "Manual Therapy", enrolled: 220, completed: 180, pass_rate: 85 },
  { name: "Geriatric Care", enrolled: 190, completed: 160, pass_rate: 88 },
  { name: "Pediatric Therapy", enrolled: 150, completed: 120, pass_rate: 90 },
  { name: "Stroke Rehab", enrolled: 180, completed: 140, pass_rate: 82 },
];

export default function AdminLMS() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/therapists`, { withCredentials: true });
        setTherapists(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const avgCompletion = therapists.length > 0 
    ? Math.round(therapists.reduce((sum, t) => sum + (t.lms_completion || 0), 0) / therapists.length) 
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-lms">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Enrolled</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{therapists.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Avg. Completion</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{avgCompletion}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Failed Exams</p>
                <p className="text-2xl font-heading font-bold text-slate-900">12</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <Award className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Certs Expiring</p>
                <p className="text-2xl font-heading font-bold text-slate-900">8</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Module Completion Chart */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Module Completion Rates</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={lmsModules} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Pass Rate']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Bar dataKey="pass_rate" fill="#F40d07" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Module Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              Training Modules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {lmsModules.map((module, i) => (
                <div key={i} className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-slate-900">{module.name}</p>
                    <Badge className="bg-emerald-100 text-emerald-700">{module.pass_rate}% pass</Badge>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-600">
                    <span>{module.enrolled} enrolled</span>
                    <span>•</span>
                    <span>{module.completed} completed</span>
                  </div>
                  <Progress value={(module.completed / module.enrolled) * 100} className="h-2 mt-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Therapist LMS Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Therapist LMS Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Therapist</TableHead>
                <TableHead>Role Type</TableHead>
                <TableHead>Modules Completed</TableHead>
                <TableHead>Certifications</TableHead>
                <TableHead>Overall Progress</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {therapists.slice(0, 10).map((th) => (
                <TableRow key={th.therapist_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={th.picture} />
                        <AvatarFallback className="bg-primary text-white text-sm">
                          {th.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{th.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-600">{th.role_type}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{Math.round((th.lms_completion || 0) / 20)} / 5</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {th.certifications?.slice(0, 2).map((cert, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{cert}</Badge>
                      ))}
                      {(th.certifications?.length || 0) > 2 && (
                        <Badge variant="secondary" className="text-xs">+{th.certifications.length - 2}</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress value={th.lms_completion || 0} className="w-20 h-2" />
                      <span className="text-xs text-slate-500">{th.lms_completion || 0}%</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={
                      (th.lms_completion || 0) >= 80 ? "bg-emerald-100 text-emerald-700" :
                      (th.lms_completion || 0) >= 50 ? "bg-amber-100 text-amber-700" :
                      "bg-red-100 text-red-700"
                    }>
                      {(th.lms_completion || 0) >= 80 ? "Complete" : (th.lms_completion || 0) >= 50 ? "In Progress" : "Behind"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
